import {
	BN,
	BigNum,
	VelocityClient,
	MAX_LEVERAGE_ORDER_SIZE,
	MarketType,
	OptionalOrderParams,
	PositionDirection,
	PublicKey,
	SpotMarketAccount,
	User,
	UserAccount,
	ZERO,
	OrderType,
	PerpMarketAccount,
	SlotDurationMs,
} from '@velocity-exchange/sdk';
import {
	MarketId,
	TradeOffsetPrice,
	UIMarket,
	OptionalAuctionParamsRequestInputs,
	OptionalTriggerOrderParams,
	AuctionParams,
} from '@velocity-exchange/common';
import { UserSettings } from '../../../../environmentVariables/EnvironmentVariableTypes';
import {
	DEFAULT_MARKET_AUCTION_DURATION_SECONDS,
	syncGetCurrentSettings,
} from '../../../../environmentVariables/EnvironmentVariables';
import { auctionDurationSecondsToSlots } from 'src/utils/auctionDuration';
import { AuctionToastEvent } from '../../../../utils/VelocityAppEvents';
import {
	getAdditionalEndPriceBuffer,
	isForceAuctionEndPriceUpToSlippage,
} from 'src/utils/slippage';
import { generateUserOrderIdForUser } from 'src/utils/order';
import { DerivedMarketTradeState } from 'src/stores/useMarketStateStore';
import useFeatureFlagStore from 'src/stores/featureFlag/useFeatureFlagStore';
import useVelocityAccountStore from '../../../useVelocityAccountsStore';
import {
	getOrderParams,
	type ProcessedOrderParamsResponse,
} from 'src/utils/orderParamsClient';
import { SlippageTolerance } from 'src/@types/types';
import { buildLimitAuctionParams } from 'src/components/TradeForm/utils/buildLimitAuctionParams';
import {
	CommonTradeFormOutputOrderConstructionProps,
	PerpTradeFormOutputProps,
} from './types';
import { OrderToastProps, TRADE_PREP_UTILS } from './tradePrep';

// Combined return type for order params and toast props (spot)
export interface SpotOrderParamsWithToastProps {
	paramsResponse: ProcessedOrderParamsResponse;
	orderToastProps: OrderToastProps;
	userOrderId: number;
}

// Strict interfaces for spot orders to match perp pattern
export interface StrictSpotBasicMarketOrderParams {
	reduceOnly: boolean;
	orderType: OrderType;
	marketIndex: number;
	baseAssetAmount: BN;
	direction: PositionDirection;
	marketType: MarketType;
	price: BN;
	auctionDuration: number | null;
	auctionStartPrice: BN | null;
	auctionEndPrice: BN | null;
	oraclePriceOffset: BN | null;
	userOrderId: number;
}

export interface StrictSpotOracleMarketOrderParams {
	reduceOnly: boolean;
	orderType: OrderType;
	marketIndex: number;
	baseAssetAmount: BN;
	direction: PositionDirection;
	marketType: MarketType;
	auctionDuration: number | null;
	auctionStartPrice: BN | null;
	auctionEndPrice: BN | null;
	oraclePriceOffset: BN | null;
	userOrderId: number;
}

export interface SpotTradeFormOutputProps extends CommonTradeFormOutputOrderConstructionProps {
	targetMarketIndex: number;
	currentPositionBaseSize: BN;
	currentPositionDirection: 'long' | 'short';
	spotMarketAccount: SpotMarketAccount;
}

export type OrderPrepHarnessProps = {
	getUserAccountData: () => {
		client: User | undefined;
		pubKey: PublicKey;
	};
	getVelocityClient: () => VelocityClient | undefined;
	getPriceInfo: (marketId: MarketId) => DerivedMarketTradeState;
	getOraclePriceData: (marketId: MarketId) => BN;
	/** Live slot duration, for turning the trader's wall-clock ramp into slots. */
	slotDuration: SlotDurationMs;
};

export const getNextUserOrderIdForCurrentUser = () => {
	const currentUser =
		useVelocityAccountStore.getState().accounts[
			useVelocityAccountStore.getState().currentUserKey ?? ''
		]?.client; // TODO :: Consider making User an input prop;

	if (!currentUser) {
		return 0; // Handle potential edge cases where we are generating an order before there is a current user initialized
	}

	return generateUserOrderIdForUser(
		useVelocityAccountStore.getState().accounts[
			useVelocityAccountStore.getState().currentUserKey ?? ''
		]?.client as User // TODO :: Consider making User an input prop
	);
};

// ### SPOT

interface OrderParamsWithUserOrderId extends OptionalOrderParams {
	userOrderId: number;
}

export interface FinalPreppedSpotMarketOrderProps {
	orderParams: OrderParamsWithUserOrderId;
	userAccountPublicKey: PublicKey;
	user: User;
	userAccount: UserAccount;
	auctionToastEventProps: AuctionToastEvent;
	entryPrice: BN;
	oraclePrice: BN;
	nextOrderId: number;
	subAccountId: number;
	attemptPlaceAndTake: boolean;
	toastId: string;
	marketName: string;
}

const prepSpotMarketOrderParams = async (
	inputProps: SpotTradeFormOutputProps,
	harnessProps: OrderPrepHarnessProps
): Promise<FinalPreppedSpotMarketOrderProps> => {
	const velocityClient = harnessProps.getVelocityClient();
	const userData = harnessProps.getUserAccountData();
	const processedProps = await processSpotTradeFormOutput(
		inputProps,
		harnessProps
	);
	const currentSettings =
		inputProps?.currentSettings ?? syncGetCurrentSettings();

	// Get oracle price for tracking
	const marketId = MarketId.createSpotMarket(inputProps.targetMarketIndex);
	const oraclePrice = harnessProps.getOraclePriceData(marketId);

	const isOracleOrder = currentSettings?.oracleOffsetOrdersEnabled ?? false;
	const orderParams = isOracleOrder
		? getStrictSpotOracleOrderParams(
				processedProps.paramsResponse,
				processedProps.userOrderId
			)
		: getStrictSpotMarketOrderParams(
				processedProps.paramsResponse,
				processedProps.userOrderId
			);

	const attemptPlaceAndTake =
		velocityClient?.txVersion === 0 && currentSettings?.placeAndTakeEnabled;

	const targetMarket = UIMarket.spotMarkets.find(
		(market) => market.marketIndex === inputProps.targetMarketIndex
	);

	const marketName = targetMarket?.symbol;

	return {
		orderParams,
		userAccountPublicKey: userData.pubKey,
		userAccount: userData.client?.getUserAccountOrThrow() as UserAccount,
		auctionToastEventProps: processedProps.orderToastProps,
		entryPrice: processedProps.paramsResponse.priceImpact.entryPrice,
		oraclePrice,
		nextOrderId: userData.client?.getUserAccountOrThrow().nextOrderId as number,
		subAccountId: userData.client?.getUserAccountOrThrow()
			.subAccountId as number,
		user: userData.client as User,
		attemptPlaceAndTake: attemptPlaceAndTake as boolean,
		toastId: processedProps.orderToastProps.toastId,
		marketName: marketName as string,
	};
};

const processSpotTradeFormOutput = async (
	inputProps: SpotTradeFormOutputProps,
	harnessProps: OrderPrepHarnessProps
): Promise<SpotOrderParamsWithToastProps> => {
	const direction =
		inputProps.side === 'buy'
			? PositionDirection.LONG
			: PositionDirection.SHORT;

	const formattedBaseAmount =
		TRADE_PREP_UTILS.spot.formatTradeFormFieldValue.baseSize(
			inputProps.baseSizeStringValue as string,
			inputProps.spotMarketAccount
		);

	const currentSettings =
		inputProps?.currentSettings ?? syncGetCurrentSettings();

	const marketId = MarketId.createSpotMarket(inputProps.targetMarketIndex);

	const nextUserOrderId = getNextUserOrderIdForCurrentUser();

	const orderToastProps = TRADE_PREP_UTILS.getOrderToastProps({
		marketId,
		direction,
		baseSize: formattedBaseAmount,
		numOrders: 1,
		currentSettings: currentSettings ?? {},
		orderType: 'market',
		userOrderId: nextUserOrderId,
	});

	const forceAuctionEndPriceUpToSlippage = isForceAuctionEndPriceUpToSlippage(
		inputProps.slippageTolerance as SlippageTolerance,
		marketId
	);

	// Create getter functions for fallback parameters
	const velocityClient = harnessProps.getVelocityClient();

	if (!velocityClient) {
		throw new Error('Drift client is not initialized');
	}

	const orderParams = await getOrderParams({
		assetType: 'base',
		marketType: MarketType.SPOT,
		marketIndex: marketId.marketIndex,
		direction,
		maxLeverageSelected: inputProps.maxLeverageSelected || false,
		maxLeverageOrderSize: MAX_LEVERAGE_ORDER_SIZE,
		amount: formattedBaseAmount.val.abs(),
		reduceOnly: inputProps.reduceOnly || false,
		auctionDurationSeconds:
			currentSettings?.auctionDurationSeconds ??
			DEFAULT_MARKET_AUCTION_DURATION_SECONDS,
		slotDuration: harnessProps.slotDuration,
		auctionStartPriceOffset:
			TRADE_PREP_UTILS.getMarketBasedAuctionStartPriceOffset(
				currentSettings?.auctionStartPriceOffset as number,
				marketId
			),
		auctionEndPriceOffset: currentSettings?.auctionEndPriceOffset as number,
		auctionStartPriceOffsetFrom:
			TRADE_PREP_UTILS.getMarketBasedAuctionStartPriceOffsetFrom(
				currentSettings?.auctionStartPriceOffsetFrom as TradeOffsetPrice,
				marketId
			),
		auctionEndPriceOffsetFrom:
			currentSettings?.auctionEndPriceOffsetFrom as TradeOffsetPrice,
		slippageTolerance: inputProps.slippageTolerance as SlippageTolerance,
		isOracleOrder: currentSettings?.oracleOffsetOrdersEnabled ?? false,
		additionalEndPriceBuffer: ZERO,
		forceUpToSlippage: forceAuctionEndPriceUpToSlippage,
		// Add fallback parameters
		velocityClient: velocityClient,
		orderType: inputProps.orderType as 'market' | 'oracle',
	});

	return {
		paramsResponse: orderParams,
		orderToastProps,
		userOrderId: nextUserOrderId,
	};
};

const getStrictSpotMarketOrderParams = (
	paramsResponse: ProcessedOrderParamsResponse,
	userOrderId: number
): StrictSpotBasicMarketOrderParams => {
	return {
		price: paramsResponse.auctionParams.auctionEndPrice ?? ZERO,
		reduceOnly: paramsResponse.auctionParams.reduceOnly ?? false,
		orderType: paramsResponse.auctionParams.orderType,
		marketIndex: paramsResponse.auctionParams.marketIndex,
		baseAssetAmount: paramsResponse.auctionParams.baseAssetAmount,
		direction: paramsResponse.auctionParams.direction,
		marketType: MarketType.SPOT,
		auctionDuration: paramsResponse.auctionParams.auctionDuration ?? null,
		auctionStartPrice: paramsResponse.auctionParams.auctionStartPrice ?? null,
		auctionEndPrice: paramsResponse.auctionParams.auctionEndPrice ?? null,
		oraclePriceOffset: paramsResponse.auctionParams.oraclePriceOffset ?? null,
		userOrderId,
	};
};

const getStrictSpotOracleOrderParams = (
	paramsResponse: ProcessedOrderParamsResponse,
	userOrderId: number
): StrictSpotOracleMarketOrderParams => {
	return {
		reduceOnly: paramsResponse.auctionParams.reduceOnly ?? false,
		orderType: paramsResponse.auctionParams.orderType,
		marketIndex: paramsResponse.auctionParams.marketIndex,
		baseAssetAmount: paramsResponse.auctionParams.baseAssetAmount,
		direction: paramsResponse.auctionParams.direction,
		marketType: MarketType.SPOT,
		auctionDuration: paramsResponse.auctionParams.auctionDuration ?? null,
		auctionStartPrice: paramsResponse.auctionParams.auctionStartPrice ?? null,
		auctionEndPrice: paramsResponse.auctionParams.auctionEndPrice ?? null,
		oraclePriceOffset: paramsResponse.auctionParams.oraclePriceOffset ?? null,
		userOrderId,
	};
};

/**
 * Gets limit order auction params by fetching the calculated auction start price
 * from the order params client, then calling getLimitAuctionParams
 */
const getLimitOrderAuctionParams = async ({
	marketId,
	limitPrice,
	direction,
	harnessProps,
	amount,
	maxLeverageSelected = false,
	reduceOnly = false,
	settings,
	slippageTolerance,
}: {
	marketId: MarketId;
	limitPrice: BigNum;
	direction: PositionDirection;
	harnessProps: OrderPrepHarnessProps;
	amount: BN;
	maxLeverageSelected?: boolean;
	reduceOnly?: boolean;
	settings?: Partial<UserSettings>;
	slippageTolerance: SlippageTolerance;
}): Promise<AuctionParams> => {
	const currentSettings = settings ?? syncGetCurrentSettings();

	// Create getter functions for fallback parameters
	const velocityClient = harnessProps.getVelocityClient();

	if (!velocityClient) {
		throw new Error(
			'VelocityClient is required for getLimitOrderAuctionParams'
		);
	}

	// Utilize the auction params calculator just to fetch what the auctionStartPrice WOULD be for a market order
	// If it is better than the limit price, we will use this for auction params
	const orderParams = await getOrderParams({
		assetType: 'base',
		marketType: marketId.marketType,
		marketIndex: marketId.marketIndex,
		direction,
		maxLeverageSelected,
		maxLeverageOrderSize: MAX_LEVERAGE_ORDER_SIZE,
		amount,
		reduceOnly,
		auctionDurationSeconds:
			currentSettings?.auctionDurationSeconds ??
			DEFAULT_MARKET_AUCTION_DURATION_SECONDS,
		slotDuration: harnessProps.slotDuration,
		auctionStartPriceOffset:
			TRADE_PREP_UTILS.getMarketBasedAuctionStartPriceOffset(
				currentSettings?.auctionStartPriceOffset ?? 'marketBased',
				marketId
			),
		auctionEndPriceOffset: currentSettings?.auctionEndPriceOffset ?? 0,
		auctionStartPriceOffsetFrom:
			TRADE_PREP_UTILS.getMarketBasedAuctionStartPriceOffsetFrom(
				currentSettings?.auctionStartPriceOffsetFrom ?? 'marketBased',
				marketId
			),
		auctionEndPriceOffsetFrom:
			currentSettings?.auctionEndPriceOffsetFrom ?? 'oracle',
		slippageTolerance,
		isOracleOrder: false,
		additionalEndPriceBuffer: marketId.isPerp
			? getAdditionalEndPriceBuffer(
					velocityClient.getPerpMarketAccount(marketId.marketIndex)
				)
			: ZERO,
		forceUpToSlippage: false,
		orderType: 'market',
		velocityClient,
	});

	// Use the calculated auction start price from the order params response
	const auctionStartPrice =
		orderParams.auctionParams?.auctionStartPrice ?? ZERO;

	// Use buildLimitAuctionParams with the new parameters
	return buildLimitAuctionParams({
		velocityClient,
		direction,
		marketId,
		inputPrice: limitPrice,
		auctionStartPrice,
		oraclePrice: orderParams.oraclePrice,
		slotDuration: harnessProps.slotDuration,
	});
};

const getOptionalAuctionParamsInputs = ({
	maxLeverageSelected,
	uiMarket,
	slippageTolerance,
	overrideIsOracleOrder,
	marketAccount,
	slotDuration,
}: {
	maxLeverageSelected: boolean;
	uiMarket: UIMarket;
	slippageTolerance: SlippageTolerance;
	overrideIsOracleOrder?: {
		isOracleOrder: boolean;
	};
	marketAccount: PerpMarketAccount | SpotMarketAccount;
	slotDuration: SlotDurationMs;
}): OptionalAuctionParamsRequestInputs => {
	const currentSettings = syncGetCurrentSettings();

	const additionalEndPriceBuffer = uiMarket.isPerp
		? getAdditionalEndPriceBuffer(marketAccount as PerpMarketAccount)
		: ZERO;

	// Omit auctionDuration when using the default so the server can derive it
	// from `version` instead of the client forcing a value.
	const isUsingDefaultAuctionDuration =
		currentSettings?.auctionDurationSeconds === undefined ||
		currentSettings.auctionDurationSeconds ===
			DEFAULT_MARKET_AUCTION_DURATION_SECONDS;

	return {
		maxLeverageSelected: maxLeverageSelected,
		maxLeverageOrderSize: MAX_LEVERAGE_ORDER_SIZE,
		auctionDuration: isUsingDefaultAuctionDuration
			? undefined
			: auctionDurationSecondsToSlots(
					currentSettings.auctionDurationSeconds,
					slotDuration
				),
		auctionStartPriceOffset:
			TRADE_PREP_UTILS.getMarketBasedAuctionStartPriceOffset(
				currentSettings?.auctionStartPriceOffset as number | 'marketBased',
				uiMarket.marketId
			),
		auctionEndPriceOffset: currentSettings?.auctionEndPriceOffset ?? 0,
		auctionStartPriceOffsetFrom:
			TRADE_PREP_UTILS.getMarketBasedAuctionStartPriceOffsetFrom(
				currentSettings?.auctionStartPriceOffsetFrom as
					| TradeOffsetPrice
					| 'marketBased',
				uiMarket.marketId
			),
		auctionEndPriceOffsetFrom: currentSettings?.auctionEndPriceOffsetFrom,
		slippageTolerance,
		isOracleOrder: overrideIsOracleOrder
			? overrideIsOracleOrder.isOracleOrder
			: (currentSettings?.oracleOffsetOrdersEnabled ?? false),
		additionalEndPriceBuffer,
		forceUpToSlippage: isForceAuctionEndPriceUpToSlippage(
			slippageTolerance,
			uiMarket.marketId
		),
		version:
			useFeatureFlagStore.getState().flags.AUCTION_PARAMS_VERSION.version,
	};
};

const getExtraInfoFromPerpTradeFormOutputProps = (
	perpTradeFormOutputProps: PerpTradeFormOutputProps & {
		leadSide: 'base' | 'quote';
	},
	slotDuration: SlotDurationMs,
	overrideIsOracleOrder?: {
		isOracleOrder: boolean;
	}
) => {
	const perpMarketAccount = perpTradeFormOutputProps.perpMarketAccount;

	const direction =
		perpTradeFormOutputProps.side === 'buy'
			? PositionDirection.LONG
			: PositionDirection.SHORT;

	const parsedBaseAmount =
		TRADE_PREP_UTILS.perp.formatTradeFormFieldValue.baseSize(
			perpTradeFormOutputProps.baseSizeStringValue ?? '0'
		);
	const baseAmountBigNum = TRADE_PREP_UTILS.perp.adjustForStepSize(
		parsedBaseAmount,
		perpMarketAccount
	);

	const bracketOrders: {
		takeProfit?: OptionalTriggerOrderParams;
		stopLoss?: OptionalTriggerOrderParams;
	} = {};

	if (perpTradeFormOutputProps.bracketOrders?.takeProfit) {
		const takeProfitParams = perpTradeFormOutputProps.bracketOrders.takeProfit;

		bracketOrders.takeProfit = {
			triggerPrice: takeProfitParams.price.val,
			baseAssetAmount: baseAmountBigNum.val,
			reduceOnly: true,
		};
	}
	if (perpTradeFormOutputProps.bracketOrders?.stopLoss) {
		const stopLossParams = perpTradeFormOutputProps.bracketOrders.stopLoss;

		bracketOrders.stopLoss = {
			triggerPrice: stopLossParams.price.val,
			baseAssetAmount: baseAmountBigNum.val,
			reduceOnly: true,
		};
	}

	const numOfOrders =
		1 + (bracketOrders.takeProfit ? 1 : 0) + (bracketOrders.stopLoss ? 1 : 0);

	const uiMarket = UIMarket.createPerpMarket(
		perpTradeFormOutputProps.targetMarketIndex
	);
	const optionalAuctionParamsInputs = getOptionalAuctionParamsInputs({
		maxLeverageSelected: !!perpTradeFormOutputProps.maxLeverageSelected,
		uiMarket,
		slippageTolerance:
			perpTradeFormOutputProps.slippageTolerance as SlippageTolerance,
		marketAccount: perpMarketAccount,
		overrideIsOracleOrder: overrideIsOracleOrder,
		slotDuration,
	});

	return {
		direction,
		baseAmountBigNum,
		bracketOrders,
		numOfOrders,
		optionalAuctionParamsInputs,
	};
};

export const ORDER_PREP_UTILS = {
	prepSpotMarketOrderParams,
	getLimitOrderAuctionParams,
	getOptionalAuctionParamsInputs,
	getExtraInfoFromPerpTradeFormOutputProps,
	getNextUserOrderIdForCurrentUser,
};
